import { useEffect, useState } from "react";
import httpService from "../../services/httpService";
import Spinner from "react-bootstrap/Spinner";
import Collapse from "react-bootstrap/Collapse";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { FaPlus, FaTimes, FaLock } from "react-icons/fa";
import '../../styles/PracticeModal.scss';

const PracticeSkillsSelector = ({ podcastClubGuid, keyTakeawayId, practice, isCompleted, onPracticeUpdated }) => {
    const [availableSkills, setAvailableSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(new Set());
    const [showAddForm, setShowAddForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [savingCustom, setSavingCustom] = useState(false);

    useEffect(() => {
        if (!podcastClubGuid) return;
        httpService.get(`/dash/club/${podcastClubGuid}/skills`)
            .then((res) => setAvailableSkills(res.data.skills || []))
            .catch((err) => console.error('Error fetching skills:', err))
            .finally(() => setLoading(false));
    }, [podcastClubGuid]);

    const isAttached = (skill) =>
        (practice?.skills || []).some((s) => s.id === skill.id);

    const handleToggle = (skill) => {
        if (toggling.has(skill.id)) return;
        setToggling((prev) => new Set(prev).add(skill.id));

        const url = `/dash/key-takeaway/${keyTakeawayId}/practices/${practice.id}/skills/${skill.id}`;
        const req = isAttached(skill)
            ? httpService.delete(url)
            : httpService.post(url);

        req
            .then((res) => onPracticeUpdated(res.data.data))
            .catch((err) => console.error('Error toggling skill:', err))
            .finally(() => {
                setToggling((prev) => {
                    const next = new Set(prev);
                    next.delete(skill.id);
                    return next;
                });
            });
    };

    const handleAddCustomSkill = () => {
        if (!newTitle.trim()) return;
        setSavingCustom(true);
        httpService.post(`/dash/club/${podcastClubGuid}/listener-skills`, {
            title: newTitle.trim(),
            description: newDesc.trim() || undefined,
        })
            .then((res) => {
                const skill = res.data.skill;
                setAvailableSkills((prev) => [...prev, skill]);
                const attachUrl = `/dash/key-takeaway/${keyTakeawayId}/practices/${practice.id}/skills/${skill.id}`;
                return httpService.post(attachUrl);
            })
            .then((res) => {
                onPracticeUpdated(res.data.data);
                setNewTitle('');
                setNewDesc('');
                setShowAddForm(false);
            })
            .catch((err) => console.error('Error creating/attaching custom skill:', err))
            .finally(() => setSavingCustom(false));
    };

    if (loading) return <Spinner animation="border" size="sm" />;
    if (!podcastClubGuid) return null;

    const visibleSkills = isCompleted
        ? availableSkills.filter(isAttached)
        : availableSkills;

    return (
        <div className="mt-3">
            <small className="text-muted d-block mb-2">Skills you practice</small>
            <div className="d-flex flex-wrap gap-2">
                {visibleSkills.map((skill) => {
                    const attached = isAttached(skill);
                    const busy = toggling.has(skill.id);
                    return (
                        <div
                            key={skill.guid}
                            className={`skill-tile${attached ? ' skill-tile--attached' : ''}${skill.is_custom ? ' skill-tile--custom' : ''}`}
                        >
                            {skill.is_custom && <span className="skill-tile__custom-badge">Your skill</span>}
                            <div className="skill-tile__title">{skill.title}</div>
                            {skill.description && <div className="skill-tile__desc">{skill.description}</div>}
                            {!isCompleted && (
                                <button
                                    type="button"
                                    className={`skill-tile__btn skill-tile__btn--${attached ? 'detach' : 'attach'}`}
                                    disabled={busy}
                                    onClick={() => handleToggle(skill)}
                                >
                                    {busy
                                        ? <Spinner animation="border" size="sm" style={{ width: '0.6rem', height: '0.6rem' }} />
                                        : attached ? <FaTimes /> : <FaPlus />
                                    }
                                    {attached ? 'Remove' : 'Add'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="skill-custom-info">
                <FaLock style={{ fontSize: '0.6rem' }} />
                Custom skills are private to you — other listeners won't see them.
            </div>
            {!isCompleted && (
            <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => setShowAddForm((v) => !v)}>
                <FaPlus className="me-1" />Add your own skill
            </Button>
            )}
            <Collapse in={showAddForm}>
                <div>
                    <div className="skill-add-form mt-2">
                        <Form.Group className="mb-2">
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Skill title (required)"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Control
                                size="sm"
                                as="textarea"
                                rows={2}
                                placeholder="Description (optional)"
                                value={newDesc}
                                onChange={(e) => setNewDesc(e.target.value)}
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={handleAddCustomSkill}
                                disabled={savingCustom || !newTitle.trim()}
                            >
                                {savingCustom ? <Spinner animation="border" size="sm" style={{ width: '0.7rem', height: '0.7rem' }} className="me-1" /> : null}
                                Save Skill
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShowAddForm(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

export default PracticeSkillsSelector;
